import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { submitEnquiry, ApiError } from "../services/api";
import usePageTracking from "../hooks/usePageTracking";
import { trackEvent } from '../services/mixpanel';
const EnquiryForm = ({
  title,
  content,
  backgroundImage,
  tagLine,
}) => {
  const pageInfo = usePageTracking();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    category: "",
    interest: "Buying",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const isFormValid =
    formData.name.trim().length > 0 && formData.phone.trim().length > 0;
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (formData.phone.length < 10) {
      newErrors.phone = "Please enter a valid phone number";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  React.useEffect(() =>{
    trackEvent("", {
      form_type:"Enquiry Form"
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      trackEvent('Enquiry Form Validation Failed', {
      fields_with_errors: Object.keys(newErrors),
      error_count: Object.keys(newErrors).length,
      form_type: 'Enquiry Form'
    });
      return;
    }

    setIsSubmitting(true);

    try {

      // Add page tracking information to form data
      const submissionData = {
        ...formData,
        source_page: {
          url: pageInfo.url,
          pathname: pageInfo.pathname,
          search: pageInfo.search,
          title: pageInfo.title,
          referrer: pageInfo.referrer,
          timestamp: new Date().toISOString()
        }
      };

      // Submit the enquiry using the API service
      await submitEnquiry(submissionData);

      trackEvent('Enquiry Form Submitted', {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      interest: formData.interest,
      category: formData.category || 'Not selected',
      form_type: 'Enquiry Form'
    });

      // Reset form on successful submission
      setFormData({
        name: "",
        phone: "",
        email: "",
        category: "",
        interest: "Buying",
      });

      // Show success message instead of redirecting
      setShowSuccessMessage(true);
    } catch (error) {
      console.error("Submission error:", error);

      trackEvent('Enquiry Form Submission Failed', {
      name: formData.name,
      error_message: error?.message || 'Unknown error',
      form_type: 'EnquiryForm'
    });
      
      let errorMessage = "Something went wrong. Please try again.";
      if (error instanceof ApiError) {
        errorMessage = error.message;
      }
      
      // For now, just log the error. Could be enhanced with proper error handling UI
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="main-form overflow-hidden flex flex-col lg:flex-row rounded-md" style={{boxShadow: '0px 0px 44px 0px #00000033'
}}>
      {/* Left Hero Section */}
      <div className="form-sec lg:w-1/2 relative bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center px-2 sm:px-8 md:px-12 lg:px-[60px] py-8 sm:py-12 md:py-16 lg:py-[64px] min-h-[300px] sm:min-h-[400px] lg:min-h-auto">
        {/* Background Image Overlay */}
        <div
          className="form-sec-img absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url(${backgroundImage})`,
          }}
        ></div>

        {/* Content */}
        <div className="form-sec-content relative text-white">
          <h1 className="text-4xl sm:text-3xl md:text-4xl lg:text-5xl font-normal mb-4 sm:mb-6 lg:leading-tight">
            {title}
          </h1>
          <div>
          {content}
          </div>
          <div className="hidden mt-4 sm:mt-6 sm:flex items-center justify-end">
            <img 
              className="w-full max-w-[50px] sm:max-w-[60px] md:max-w-[70px] h-auto object-contain" 
              src="images/logos/formLogo.png" 
              alt="Golden Sparrow" 
            />
          </div>
        </div>
      </div>

      {/* Right Form Section */}
      <div className="form-sec-form lg:w-1/2 bg-white flex items-center justify-center">
        <div className="w-full">
          {!showSuccessMessage ? (
          <div className="bg-white px-4 py-8 md:p-12">
            <h2 className="text-2xl sm:text-3xl md:text-[32px] lg:text-[40px] font-semibold text-gray-800 mb-6 sm:mb-8">
              Enquire Now
            </h2>
            <form onSubmit={handleSubmit} className="" noValidate>
                {/* Name Field */}
                <div className="py-1">
                  <label
                    htmlFor="name"
                    className="block text-sm font-bold text-gray-700 mb-2"
                  >
                    Your Name{" "}
                    <span className="text-red-500" aria-label="required">
                      *
                    </span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD200] transition-colors ${
                      errors.name ? "border-red-500 bg-red-50" : "border-gray-300"
                    }`}
                    style={{
                      height: "44px",
                      lineHeight: "1.5",
                      color: "#1f2937",
                    }}
                    autoComplete="off"
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                {/* Phone and Email Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 py-1 mt-2 sm:mt-4">
                  {/* Phone Field */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Phone number{" "}
                      <span className="text-red-500" aria-label="required">
                        *
                      </span>
                    </label>
                    <PhoneInput
                      country={"ae"}
                      value={formData.phone}
                      onChange={(phone) =>
                        setFormData((prev) => ({ ...prev, phone }))
                      }
                      onFocus={() => {
                        if (errors.phone) {
                          setErrors((prev) => ({ ...prev, phone: "" }));
                        }
                      }}
                      inputStyle={{
                        width: "100%",
                        height: "44px",
                        fontSize: "16px",
                        padding: "12px 16px",
                        paddingLeft: "48px", // ✅ Space for country flag
                        borderColor: errors.phone ? "#ef4444" : "#d1d5db",
                        backgroundColor: errors.phone ? "#fef2f2" : "#ffffff",
                        borderRadius: "8px",
                        fontFamily: "inherit",
                        lineHeight: "1.5",
                        color: "#1f2937",
                      }}
                      buttonStyle={{
                        borderColor: errors.phone ? "#ef4444" : "#d1d5db",
                        backgroundColor: "#f9fafb",
                        borderRadius: "8px 0 0 8px",
                      }}
                      dropdownStyle={{
                        maxHeight: "200px",
                      }}
                      searchStyle={{
                        padding: "8px",
                        fontSize: "14px",
                      }}
                      enableSearch={true}
                      searchPlaceholder="Search countries"
                      placeholder="Enter phone number" // ✅ Ensure placeholder is set
                      specialLabel=""
                    />
                    {errors.phone && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600" role="alert">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-bold text-gray-700 mb-2"
                    >
                      Customer Email ID{" "}
                      <span className="text-red-500" aria-label="required">
                        *
                      </span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      autoComplete="off"
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD200] transition-colors placeholder-gray-500 ${
                        errors.email
                          ? "border-red-500 bg-red-50"
                          : "border-gray-300"
                      }`}
                      style={{
                        height: "44px",
                        lineHeight: "1.5",
                        color: "#1f2937",
                      }}
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>
                </div>

                {/* Interest Selection */}
                <fieldset className="bg-[#f5f1e6] rounded-lg p-3 sm:p-4 w-full max-w-[450px] mx-auto mt-6 sm:mt-12">
                  <div className="flex flex-col justify-center items-center sm:flex-row gap-3 sm:gap-4 md:gap-6">
                    <p className="text-xs sm:text-sm font-semibold text-[#9E3F2E] shrink-0">
                      I am interested in:
                    </p>

                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 sm:gap-4">
                      <label className="inline-flex items-center gap-2 cursor-pointer text-xs sm:text-sm">
                        <input
                          type="radio"
                          name="interest"
                          value="Buying"
                          checked={formData.interest === "Buying"}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-[#9E3F2E] focus:ring-[#9E3F2E] border-gray-300"
                        />
                        <span>Buying</span>
                      </label>

                      <label className="inline-flex items-center gap-2 cursor-pointer text-xs sm:text-sm">
                        <input
                          type="radio"
                          name="interest"
                          value="Renting"
                          checked={formData.interest === "Renting"}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-[#9E3F2E] focus:ring-[#9E3F2E] border-gray-300"
                        />
                        <span>Renting</span>
                      </label>
                    </div>
                  </div>
                </fieldset>
                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || !isFormValid}
                  className={`w-full sm:w-3/4 md:w-1/2 mx-auto flex justify-center items-center  font-semibold py-3 px-4 sm:px-6 rounded-md transition-colors duration-200 mt-6 sm:mt-12 text-sm sm:text-base ${
                    isFormValid && !isSubmitting
                      ? "bg-[#1A1A1A] hover:bg-gray-700 text-white"
                      : "bg-[#1a1a1a85] text-white cursor-not-allowed"
                  }`}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
                {/* Footer Text */}
              {tagLine}
              </form>
          </div>
          ) : (
          <div className="submit-message flex flex-col justify-center items-center text-center p-6 md:p-12 min-h-[400px]">
            <div className="tick-animation mb-4">
              <img
                src="/images/tick_animation.gif"
                alt="Success"
                className="w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40"
              />
            </div>
            <div className="flex flex-col justify-center items-center text-center space-y-3 max-w-lg mx-auto px-4">
              <p className="text-lg md:text-xl lg:text-[22px] font-semibold leading-relaxed">Query submitted</p>
              <p className="text-xl md:text-2xl lg:text-[32px] font-semibold leading-snug">Wallah! You're taking a worthy step towards your Business!</p>
              <h6 className="text-sm md:text-base lg:text-[16px] leading-normal font-normal">Our expert will get to you <strong>soon!</strong></h6>
            </div>
          </div>
          )}
        </div>
      </div>
      <style>{`
      @media (max-width: 1024px) {
    .main-form {
    background: none!important;
    border-radius: 0!important;
    box-shadow: none!important;
    overflow: visible!important;
}
  .form-sec {
    background: none!important;
}
.form-sec-img {
    background-image: none!important;
}
.form-sec-content h1, .form-sec-content p {
    color: #000!important;
}
.form-sec-form {
    background: #fff;
    border-radius: 14px!important;
    box-shadow: 0px 0px 18px 0px #00000032;
    overflow: hidden;
    border:2px solid #FFD200;
}
}
      `}</style>
    </div>
  );
};

export default EnquiryForm;
